import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
    MembershipTable,
    ActiveMembersTableRow,
    InvitedMembersTableRow,
    ACTIVE_MEMBERS_COL_DEFS,
    INVITED_MEMBERS_COL_DEFS,
} from '../components/MembershipTable';
import { SearchInput } from '../components/SearchInput';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';

import { useData } from '../contexts/useData';
import InviteDialog from '../components/InviteDialog';
import DeleteDialog from '../components/DeleteDialog';
import ResendDialog from '../components/ResendDialog';
import { useContainerWidth } from '@lendica/hooks';

export const MembersView = () => {
    const { isNarrow } = useContainerWidth();
    const {
        members,
        invitations,
        refreshMembers,
        refreshInvitations,
        // shared pagination
        page,
        pageSize,
        membersTotal,
        invitationsTotal,
        loadMembersPage,
        prefetchMembersNextPage,
        loadInvitationsPage,
        prefetchInvitationsNextPage,
    } = useData();
    const gridRef = useRef<AgGridReact>(null);
    const [tabValue, setTabValue] = useState(0);
    const [hasActiveFilters, setHasActiveFilters] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 400);
        return () => clearTimeout(handler);
    }, [search]);

    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<InvitedMembersTableRow | null>(null);
    const [resendDialogOpen, setResendDialogOpen] = useState(false);

    const handleResendClick = (params: InvitedMembersTableRow) => {
        setSelectedRow(params);
        setResendDialogOpen(true);
    };

    const handleRevokeClick = (params: InvitedMembersTableRow) => {
        setSelectedRow(params);
        setDeleteDialogOpen(true);
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedRow(null);
    };

    const colDefs = useMemo<(ColDef | ColGroupDef)[]>(() => {
        return tabValue === 0 ? ACTIVE_MEMBERS_COL_DEFS : INVITED_MEMBERS_COL_DEFS;
    }, [tabValue]);

    useEffect(() => {
        const fetch = async () => {
            if (tabValue === 1) {
                await loadInvitationsPage(1, pageSize, false, debouncedSearch);
                prefetchInvitationsNextPage(debouncedSearch);
            } else {
                await loadMembersPage(1, pageSize, false, debouncedSearch);
                prefetchMembersNextPage(debouncedSearch);
            }
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, tabValue]);

    const rowData = useMemo(() => {
        if (tabValue === 0) {
            if (!members) return [];
            return members!.map(member => ({
                id: member.id,
                first_name: member.user.first_name,
                last_name: member.user.last_name,
                email: member.user.email,
                role: member.user_type_display,
                active_since: member.add_time,
            }));
        } else if (tabValue === 1) {
            if (!invitations) return [];
            return invitations!.map(invitation => ({
                id: invitation.id,
                email: invitation.email,
                role: invitation.role_to_grant_display,
                status: invitation.status,
                expires_at: invitation.expires_at,
            }));
        }
    }, [tabValue, members, invitations]);

    const checkForActiveFilters = useCallback(() => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel();
            const hasFilters = Object.keys(filterModel || {}).length > 0;
            setHasActiveFilters(hasFilters);
        }
    }, []);

    const handleFilterChanged = useCallback(() => {
        checkForActiveFilters();
    }, [checkForActiveFilters]);

    const handleResetFilters = useCallback(() => {
        gridRef.current!.api.setFilterModel(null);
        setHasActiveFilters(false);
    }, []);

    const currentPage = page || 1;
    const total = tabValue === 0 ? membersTotal || 0 : invitationsTotal || 0;
    const pageCount = total && pageSize ? Math.ceil(total / pageSize) : 0;

    const currentDataLength = (rowData as any[])?.length || 0;
    const startIndex = total
        ? (currentPage - 1) * (pageSize || 0) + (currentDataLength > 0 ? 1 : 0)
        : 0;
    const endIndex = total ? (currentPage - 1) * (pageSize || 0) + currentDataLength : 0;

    const handlePrevPage = useCallback(async () => {
        if (currentPage <= 1) return;
        const next = currentPage - 1;
        if (tabValue === 0) {
            await loadMembersPage(next, pageSize, false, debouncedSearch);
            prefetchMembersNextPage(debouncedSearch);
        } else {
            await loadInvitationsPage(next, pageSize, false, debouncedSearch);
            prefetchInvitationsNextPage(debouncedSearch);
        }
    }, [
        currentPage,
        pageSize,
        tabValue,
        loadMembersPage,
        loadInvitationsPage,
        prefetchMembersNextPage,
        prefetchInvitationsNextPage,
        debouncedSearch,
    ]);

    const handleNextPage = useCallback(async () => {
        if (pageCount && currentPage >= pageCount) return;
        const next = currentPage + 1;
        if (tabValue === 0) {
            await loadMembersPage(next, pageSize, false, debouncedSearch);
            prefetchMembersNextPage(debouncedSearch);
        } else {
            await loadInvitationsPage(next, pageSize, false, debouncedSearch);
            prefetchInvitationsNextPage(debouncedSearch);
        }
    }, [
        currentPage,
        pageSize,
        pageCount,
        tabValue,
        loadMembersPage,
        loadInvitationsPage,
        prefetchMembersNextPage,
        prefetchInvitationsNextPage,
        debouncedSearch,
    ]);

    const handlePageSizeChange = useCallback(
        async (event: any) => {
            const newSize = Number(event.target.value);
            if (!newSize || newSize === pageSize) return;
            if (tabValue === 0) {
                await loadMembersPage(1, newSize, false, debouncedSearch);
                prefetchMembersNextPage(debouncedSearch);
            } else {
                await loadInvitationsPage(1, newSize, false, debouncedSearch);
                prefetchInvitationsNextPage(debouncedSearch);
            }
        },
        [
            tabValue,
            pageSize,
            loadMembersPage,
            loadInvitationsPage,
            prefetchMembersNextPage,
            prefetchInvitationsNextPage,
            debouncedSearch,
        ]
    );

    return (
        <Stack gap={isNarrow ? 1 : 4} sx={{ height: '100%' }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%', gap: isNarrow ? 1 : 0 }}
            >
                <Typography variant="h6">Team Members</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteDialogOpen(true)}
                >
                    {isNarrow ? 'Invite' : 'Invite team members'}
                </Button>
            </Stack>
            <Stack sx={{ flex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isNarrow ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isNarrow ? 'stretch' : 'center',
                        borderBottom: '1px solid #ddd',
                        gap: isNarrow ? 2 : 0,
                    }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={(_, value) => setTabValue(value)}
                        variant={isNarrow ? 'scrollable' : 'standard'}
                        scrollButtons={isNarrow ? 'auto' : false}
                        allowScrollButtonsMobile
                    >
                        <Tab
                            label={`Active members ${membersTotal > 0 ? `(${membersTotal})` : ''}`}
                            value={0}
                        />
                        <Tab
                            label={`Invited members ${
                                invitationsTotal > 0 ? `(${invitationsTotal})` : ''
                            }`}
                            value={1}
                        />
                    </Tabs>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            alignItems: 'center',
                            px: isNarrow ? 0 : 1,
                            pb: isNarrow ? 2 : 0,
                            width: isNarrow ? '100%' : 300,
                        }}
                    >
                        <SearchInput
                            placeholder="Search"
                            size="small"
                            fullWidth
                            value={search}
                            onChange={(e: any) => setSearch(e.target.value)}
                        />
                        <Tooltip title="Reset all filters" placement="top">
                            <IconButton
                                size="small"
                                color={'info'}
                                onClick={handleResetFilters}
                                sx={{
                                    'borderRadius': '4px',
                                    'backgroundColor': hasActiveFilters
                                        ? 'info.main'
                                        : 'transparent',
                                    'color': hasActiveFilters ? 'info.contrastText' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: hasActiveFilters
                                            ? 'info.dark'
                                            : 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                <FilterListOffIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                <MembershipTable
                    rowData={rowData as ActiveMembersTableRow[] | InvitedMembersTableRow[]}
                    colDefs={colDefs}
                    gridRef={gridRef}
                    onFilterChanged={handleFilterChanged}
                    onResendClick={handleResendClick}
                    onRevokeClick={handleRevokeClick}
                    useGridPagination={false}
                />
                <Divider />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                    }}
                >
                    <Typography variant="body2">Page Size:</Typography>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        color="info"
                        sx={{
                            'minWidth': 72,
                            '& .MuiSelect-select': {
                                padding: '6px 12px',
                            },
                        }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                    <Typography variant="body2" sx={{ mx: 1 }}>
                        {startIndex} to {endIndex} of {total || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ mx: 1 }}>
                        Page {pageCount ? currentPage : 0} of {pageCount || 0}
                    </Typography>
                    <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleNextPage}
                        disabled={pageCount ? currentPage >= pageCount : true}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Stack>
            <InviteDialog open={inviteDialogOpen} setOpen={setInviteDialogOpen} />
            <DeleteDialog
                open={deleteDialogOpen}
                invitationId={selectedRow?.id ?? ''}
                handleClose={handleDeleteDialogClose}
            />
            <ResendDialog
                open={resendDialogOpen}
                setOpen={setResendDialogOpen}
                email={selectedRow?.email ?? ''}
                invitationId={selectedRow?.id ?? ''}
            />
        </Stack>
    );
};
